import { useState } from "react";
import { useParams, useNavigate, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Check, Upload, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { MkCTA } from "@/components/marketing/MarketingLayout";
import { FEATURES } from "@/pages/marketing/data";

export default function FeatureDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [jobForm, setJobForm] = useState({ name: "", whatsapp: "" });
  const [resume, setResume] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const feature = FEATURES.find((f) => f.slug === slug);
  if (!feature) return <Navigate to="/features" replace />;
  const others = FEATURES.filter((f) => f.slug !== slug).slice(0, 3);

  const onResume = (e) => {
    const f = e.target.files?.[0];
    if (!f) return setResume(null);
    if (f.size > 2 * 1024 * 1024) {
      toast.error("Please upload a file under 2MB");
      e.target.value = "";
      return;
    }
    setResume(f);
  };

  const submitInterest = async (e) => {
    e.preventDefault();
    if (!jobForm.name.trim() || !jobForm.whatsapp.trim()) return toast.error("Please add your name and WhatsApp number");
    setSubmitting(true);
    try {
      let resume_name = null;
      let resume_data = null;
      if (resume) {
        resume_name = resume.name;
        resume_data = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(String(reader.result).split(",")[1]);
          reader.onerror = reject;
          reader.readAsDataURL(resume);
        });
      }
      await api.post("/jobs/interest", { name: jobForm.name.trim(), whatsapp: jobForm.whatsapp.trim(), resume_name, resume_data });
      setSubmitted(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main data-testid={`feature-detail-${slug}`}>
      <section className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 pt-14 sm:pt-20 pb-24">
        <button data-testid="back-to-features" onClick={() => navigate("/features")}
          className="flex items-center gap-2 text-sm font-medium text-slate-500 transition-colors duration-300 hover:text-[#0A1633]">
          <ArrowLeft className="w-4 h-4" /> All features
        </button>

        <div className="mt-10 grid lg:grid-cols-2 gap-14 items-center">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="w-14 h-14 rounded-2xl flex items-center justify-center" style={{ backgroundColor: `${feature.color}14` }}>
              <feature.icon className="w-7 h-7" style={{ color: feature.color }} strokeWidth={1.5} />
            </div>
            <p className="mt-6 text-sm font-semibold tracking-[0.15em] uppercase" style={{ color: feature.color }}>{feature.shortTitle}</p>
            <h1 className="mt-3 text-4xl sm:text-5xl font-bold tracking-tight text-[#0A1633] leading-tight" style={{ fontFamily: "'Outfit', sans-serif" }} data-testid="feature-title">
              {feature.tagline}
            </h1>
            <p className="mt-6 text-lg text-slate-600 leading-relaxed">{feature.heroCopy}</p>
            <div className="mt-9">
              <MkCTA testid="feature-cta">
                Unlock This for Free <ArrowRight className="inline w-4 h-4 ml-1" />
              </MkCTA>
            </div>
          </motion.div>
          <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7, delay: 0.1 }}>
            <img src={feature.image} alt={feature.title} className="rounded-3xl w-full aspect-[4/3] object-cover shadow-[0_20px_40px_rgb(0,0,0,0.08)]" />
          </motion.div>
        </div>
      </section>

      <section className="bg-white border-y border-slate-100">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-24">
          <h2 className="text-3xl font-bold tracking-tight text-[#0A1633]" style={{ fontFamily: "'Outfit', sans-serif" }}>What this means for you</h2>
          <div className="mt-10 grid sm:grid-cols-2 gap-5 max-w-4xl">
            {feature.benefits.map((b, i) => (
              <motion.div key={b} initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.45, delay: i * 0.06 }}
                data-testid={`feature-benefit-${i}`}
                className="flex items-start gap-4 bg-[#FDFDFC] border border-slate-100 rounded-2xl p-6">
                <span className="w-7 h-7 rounded-full flex items-center justify-center shrink-0 mt-0.5" style={{ backgroundColor: `${feature.color}1a` }}>
                  <Check className="w-4 h-4" style={{ color: feature.color }} strokeWidth={2.5} />
                </span>
                <p className="text-slate-700 leading-relaxed">{b}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {slug === "jobs" && (
        <section className="bg-white border-y border-slate-100" data-testid="jobs-interest-section">
          <div className="max-w-3xl mx-auto px-6 sm:px-8 lg:px-12 py-16 sm:py-24">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
              <p className="text-sm font-semibold tracking-[0.15em] uppercase text-[#D4AF37]">Corporate & high-pay part-time roles</p>
              <h2 className="mt-4 text-3xl sm:text-4xl font-bold tracking-tight text-[#0A1633]" style={{ fontFamily: "'Outfit', sans-serif" }}>
                Get matched with roles that fit you
              </h2>
              <p className="mt-4 text-lg text-slate-600 leading-relaxed">
                Leave your details and our team will reach out on WhatsApp with corporate and high-pay part-time opportunities in your city.
              </p>
              {submitted ? (
                <div className="mt-9 bg-[#FDFDFC] border border-[#10B981]/30 rounded-3xl p-8 flex items-start gap-4" data-testid="jobs-interest-success">
                  <CheckCircle2 className="w-6 h-6 text-[#10B981] shrink-0 mt-0.5" />
                  <div>
                    <p className="text-lg font-semibold text-[#0A1633]">Thanks, {jobForm.name.split(" ")[0]}! You're on the list.</p>
                    <p className="mt-2 text-slate-600 leading-relaxed">Our team will message you on WhatsApp with roles that match you. Keep an eye on your phone.</p>
                  </div>
                </div>
              ) : (
                <form onSubmit={submitInterest} className="mt-9 bg-[#FDFDFC] border border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] rounded-3xl p-7 sm:p-9 space-y-5" data-testid="jobs-interest-form">
                  <div className="grid sm:grid-cols-2 gap-5">
                    <label className="block">
                      <span className="block text-xs font-semibold tracking-[0.15em] uppercase text-slate-400 mb-2">Your name *</span>
                      <input data-testid="jobs-name-input" value={jobForm.name} onChange={(e) => setJobForm({ ...jobForm, name: e.target.value })} required
                        placeholder="Full name"
                        className="w-full bg-white border border-slate-200 rounded-2xl px-5 py-3.5 text-slate-900 placeholder-slate-400 outline-none transition-colors duration-300 focus:border-[#D4AF37]" />
                    </label>
                    <label className="block">
                      <span className="block text-xs font-semibold tracking-[0.15em] uppercase text-slate-400 mb-2">WhatsApp number *</span>
                      <input data-testid="jobs-whatsapp-input" value={jobForm.whatsapp} onChange={(e) => setJobForm({ ...jobForm, whatsapp: e.target.value })} required
                        placeholder="+44 or +91" type="tel"
                        className="w-full bg-white border border-slate-200 rounded-2xl px-5 py-3.5 text-slate-900 placeholder-slate-400 outline-none transition-colors duration-300 focus:border-[#D4AF37]" />
                    </label>
                  </div>
                  <label className="block">
                    <span className="block text-xs font-semibold tracking-[0.15em] uppercase text-slate-400 mb-2">Resume (optional, max 2MB)</span>
                    <span className="flex items-center gap-3 bg-white border border-dashed border-slate-300 rounded-2xl px-5 py-4 cursor-pointer transition-colors duration-300 hover:border-[#D4AF37]">
                      <Upload className="w-5 h-5 text-slate-400 shrink-0" />
                      <span className="text-sm text-slate-500">{resume ? resume.name : "Upload your CV (PDF or DOC)"}</span>
                      <input data-testid="jobs-resume-input" type="file" accept=".pdf,.doc,.docx" className="hidden" onChange={onResume} />
                    </span>
                  </label>
                  <button data-testid="jobs-interest-submit" type="submit" disabled={submitting}
                    className="w-full rounded-full bg-[#0A1633] text-white font-medium px-8 py-4 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_20px_40px_rgb(10,22,51,0.18)] disabled:opacity-50 focus:ring-2 focus:ring-[#D4AF37] focus:outline-none">
                    {submitting ? "Sending..." : "Notify Me About Roles"}
                  </button>
                </form>
              )}
            </motion.div>
          </div>
        </section>
      )}

      <section className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-24">
        <h2 className="text-2xl font-bold tracking-tight text-[#0A1633]" style={{ fontFamily: "'Outfit', sans-serif" }}>Members also unlock</h2>
        <div className="mt-8 grid sm:grid-cols-3 gap-6">
          {others.map((f) => (
            <button key={f.slug} data-testid={`related-${f.slug}`} onClick={() => navigate(`/features/${f.slug}`)}
              className="text-left bg-white border border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] rounded-3xl p-7 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_20px_40px_rgb(0,0,0,0.08)]">
              <f.icon className="w-6 h-6" style={{ color: f.color }} strokeWidth={1.5} />
              <h3 className="mt-4 text-lg font-semibold text-[#0A1633]" style={{ fontFamily: "'Outfit', sans-serif" }}>{f.title}</h3>
              <p className="mt-2 text-sm text-slate-600">{f.cardCopy}</p>
            </button>
          ))}
        </div>
      </section>
    </main>
  );
}
